import { useAsyncOperation } from "@core/hooks/use-async-operation";
import { getCountryLocalesFromLang } from "@core/i18n/locale-helper";
import { useRouteParams } from "@core/routing/use-route-params";
import { analyticsService, legalContentManager, localeManager, registerManager } from "@core/service-container";
import { AnalyticsCategory } from "@domain/analytics/analytics-types";
import { useSupportedLocales } from "@domain/localization/hooks/use-supported-locales";
import { LoginContainer } from "@ui/components/login/login-container";
import { RoutesPath } from "@ui/navigation/routes";
import { VipLoginForm } from "@ui/pages/login/components/vip-login-form";
import { useVipLogin } from "@ui/pages/login/hooks/use-vip-login";
import { LoadingEmptyScreen } from "@ui/pages/login/loading-empty-screen";
import React, { useCallback, useEffect, useMemo } from "react";

export default function VipLoginPage(): JSX.Element {
	const { veevaId } = useRouteParams<RoutesPath.VipLogin>();
	const { veevaUserData, loading } = useVipLogin(veevaId);
	const { supportedLocales } = useSupportedLocales({ localeManager });
	const countryLocales = useMemo(
		() => getCountryLocalesFromLang(veevaUserData?.locale, supportedLocales),
		[supportedLocales, veevaUserData?.locale],
	);

	useEffect(() => {
		analyticsService.trackEvent({ category: AnalyticsCategory.VIP, action: "reachStep" });
	}, []);

	const initVipFlow = useCallback(async () => {
		if (veevaUserData) {
			await registerManager.sendCountryCode(veevaUserData.countryCode);
			await legalContentManager.retrieveLegalDocuments(veevaUserData.locale);
		}
	}, [veevaUserData]);

	const [retrieveLegalDocuments] = useAsyncOperation(initVipFlow, {});

	useEffect(() => {
		retrieveLegalDocuments();
	}, [retrieveLegalDocuments, veevaUserData?.countryCode]);

	return loading || !veevaUserData ? (
		<LoadingEmptyScreen />
	) : (
		<LoginContainer
			form={
				<VipLoginForm
					onSuccess={() => {
						analyticsService.trackEvent({ category: AnalyticsCategory.VIP, action: "validateStep" });
					}}
					veevaUserRecordData={veevaUserData}
					countryLocales={{
						locales: countryLocales,
						defaultLocale: veevaUserData.locale,
					}}
				/>
			}
		/>
	);
}
